import { memo } from "react";
import * as Styled from "./style";
import { useSpecial } from "./use-special";
import { useRouter } from "next/router";
import { ItemEpisode } from "~/components/blocks/product/common/blocks/item-episode";
import { ItemEpisodeDetailSkeleton } from "~/components/blocks/product/common/blocks/item-episode/detail-skeleton";
import { TagList } from "~/components/blocks/product/common/blocks/tag-list";
import { PATHS } from "~/const/paths";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import { G_VideoSeasonType } from "~/lib/graphql";
import { calculatePlayProgressRate } from "~/util/calculate-play-progress-rate";
import { durationTimeText } from "~/util/duration-time-text";

/** 20話以上エピソードがなかったら`CarouselTag`を表示しない */
const MAX_TOTAL_SPECIAL_LENGTH = 20;

type SpecialProps = {
  /** 特典映像の合計数 */
  totalSpecialLength: number;
};

/**
 * 特典映像一覧を表示するコンポーネント（複数話で使用）
 * @param props {@link SpecialProps}
 */
export const Special = memo((props: SpecialProps) => {
  const { totalSpecialLength } = props;

  const { push } = useRouter();

  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "LANDSCAPE" });

  const {
    activeIndex,
    itemHeight,
    imageWidth,
    imageHeight,
    episodeTagListArray,
    isVideoSpecialEpisodeLoading,
    specialEpisodeEdges,
    episodeDownloadBtnClicked,
  } = useSpecial({ totalSpecialLength });

  return (
    <Styled.DivSpecialRoot existSpecialEpisodeTab={totalSpecialLength > MAX_TOTAL_SPECIAL_LENGTH}>
      {totalSpecialLength > MAX_TOTAL_SPECIAL_LENGTH && (
        <Styled.DivTagListCarouselBox>
          <TagList activeIndex={activeIndex} tagListArray={episodeTagListArray} />
        </Styled.DivTagListCarouselBox>
      )}

      {!isVideoSpecialEpisodeLoading && specialEpisodeEdges !== undefined ? (
        <>
          {specialEpisodeEdges.map(({ node }, index) => {
            const {
              viewingRights: { isStreamable, isDownloadable },
              playInfo: { contentId, parts, duration },
            } = node;

            const part = parts.find((part) => part.contentId === contentId);

            const progressBarRate =
              part !== undefined &&
              part?.resumePoint !== undefined &&
              part?.resumePoint !== null &&
              part?.resumePoint !== 0
                ? calculatePlayProgressRate(part.resumePoint, part.duration)
                : undefined;

            return (
              <ItemEpisode
                key={`ItemEpisode-${index}`}
                {...imageSrcFormat({ imgSrc: node.episodeImage })}
                alt={node.episodeTitle}
                episodeImageWidth={imageWidth}
                episodeImageHeight={imageHeight}
                episodeNumberName={node.episodeNumberName}
                episodeTitle={node.episodeTitle}
                episodeDescription={node.episodeDetail}
                imgSupplementText={durationTimeText(duration)}
                aspectRatio="9/16"
                visiblePlayIcon={isStreamable}
                isDownloadIcon={isDownloadable}
                // resumePointの値が「0」または「undefined」の場合は「レジュームバー」を表示しないように制御
                playedProgressRate={progressBarRate}
                onIconClick={() => {
                  const downloadModalData = {
                    titleName: node.episodeTitle,
                    seasonType: G_VideoSeasonType.MultiEpisodes,
                    downloadableFiles: node.viewingRights.downloadableFiles,
                  };

                  episodeDownloadBtnClicked(node.id, downloadModalData);
                }}
                onThumbnailClick={async () => {
                  // 視聴権利なしの場合、何も関数を発火しない
                  if (!isStreamable) return;

                  // 視聴権ありの場合、プレイヤー画面に遷移して動画を再生すること
                  void push(
                    {
                      pathname: PATHS.VOD_PLAYER,
                      query: {
                        season: node.seasonId,
                        content: node.id,
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    }
                  );
                }}
              />
            );
          })}
        </>
      ) : (
        <ItemEpisodeDetailSkeleton
          skeletonLength={20}
          oneItemWidthRatio={100}
          itemHeight={itemHeight}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        />
      )}
    </Styled.DivSpecialRoot>
  );
});

Special.displayName = "Special";
